// @flow

import React, { useState } from 'react';
import { log, formatPhoneNumberForBackend, validateEmail } from '../../utils/jsUtils';
import modelUtils from '../../utils/modelUtils';
import { useSelector, useDispatch } from 'react-redux';

import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import GroupIcon from '@mui/icons-material/LocalOffer';
import PublishIcon from '@mui/icons-material/Publish';

// import AddProperty from './AddPropertyDialog';
import FilterSearchBox from '../FilterSearchBox';
import HeaderTopBar from '../HeaderTopBar';
import SPBackend, { NOTIFICATION_CHANNELS } from '../../services/SPBackend';
import AddTenantDialog from './AddTenantDialog';
import AppLoadingSpinner from '../SharedComponents/AppLoadingSpinner';
import TagCustomersPicker from '../Feed/TagCustomersPicker';

import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/TenantsHeader.Style';

// Actions
import { 
  setSnackMessage, 
  setLoadingModule 
} from '../../reduxStore/modules/common/actions';
import { setTenantEnableUpload } from '../../reduxStore/modules/tenants/actions';
import { setAccountSettings } from '../../reduxStore/modules/user/actions';

// Selectors
import { selectUser, selectAccountSettings } from '../../reduxStore/modules/user/selectors';
import { selectTenantEnableUpload } from '../../reduxStore/modules/tenants/selectors';
import { useI18n } from '../../contexts/I18nContext';

import type { Tenant } from '../../services/SPBackend';

type Props = {
  classes: Object,
  mobileOpen: boolean,
  customerIdsSelected: Array<string>,
  usehandleDrawerToggle: Function,
  orderTenants: Function,
  handleSetSearchInputValue: Function,
  tenants: Array<Tenant>,
  setTenants: Function,
  refreshTenants: Function,
  updateTenants: Function,
  totalTenantsCount: number,
  setCountOfTenants: Function,
  searchInputValue: string,
  searchTenantValue: string
};


const TenantsHeader = (props: Props) => {
  const {
    customerIdsSelected,
    handleSetSearchInputValue,
    usehandleDrawerToggle,
    setTenants,
    updateTenants,
    totalTenantsCount,
    setCountOfTenants,
    searchTenantValue
  } = props;
  
  const theme = useTheme();
  const classes = createClasses(styles, theme);
  
  const [openAddTenantDialog, setOpenAddTenantDialog] = useState(false);
  const [openTagPicker, setOpenTagPicker] = useState(false);

  const tagPickerAnchorRef = React.useRef();

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const accountSettings = useSelector(selectAccountSettings);
  const tenantEnableUpload = useSelector(selectTenantEnableUpload);
  const i18n = useI18n();

  const handleAddTenant = (
    tenant: Tenant,
    callbackSuccess: Function,
    callbackFailure: Function
  ) => {
    log('[handleAddTenant] tenant, user: ', tenant, user);

    const formattedNb = {
      Phone: formatPhoneNumberForBackend(tenant.Phone) || ''
    };
    // validate email - if non empty
    if (tenant.Email !== '' && !validateEmail(tenant.Email)) {
      dispatch(setSnackMessage({
        message: 'Invalid email.',
        level: 'error',
        duration: 3000
      }));
      return;
    }
    if (modelUtils.getValAtPath(
      accountSettings,
      ['Tenants', 'ReceiveNotificationsBy'],
      NOTIFICATION_CHANNELS.SMS
    ) === NOTIFICATION_CHANNELS.EMAIL) {
      if (tenant.Email === '') {
        dispatch(setSnackMessage({
          message: 'A valid email is required.',
          level: 'error',
          duration: 3000
        }));
        return;
      }
    } else {
      // if account settings isn't to send email, default to SMS
      if (!formattedNb) {
        dispatch(setSnackMessage({
          message: 'Invalid phone number.',
          level: 'error',
          duration: 3000
        }));
        return;
      }
    }

    const payload = {
      ...tenant,
      ...formattedNb
    };
    
    dispatch(setLoadingModule('addCustomer', true));
    
    SPBackend.createTenant(payload, user)
      .then((response) => {
        const {data: newTenant} = response;
        log('[createTenant] res: ', newTenant);
        dispatch(setSnackMessage({
          message: `${i18n.t('Tenant')} added!`,
          level: 'success',
          duration: 5000
        }));
        dispatch(setAccountSettings({
          ...accountSettings,
          SetupRequired: {
            ...accountSettings.SetupRequired,
            Tenants: false
          }
        }));
        dispatch(setLoadingModule('addCustomer', false));
        
        setCountOfTenants(totalTenantsCount+1);
        setTenants((prev) => {
          return [newTenant, ...prev];
        });
        if (typeof callbackSuccess !== 'undefined') {
          callbackSuccess();
        }
      })
      .catch(error => {
        log('[handleAddTenant.createBuilding] error: ', error);
        if (typeof callbackFailure !== 'undefined') {
          callbackFailure();
        }
        dispatch(setSnackMessage({
          message: error.response ? error.response.data : 'An error occured. Please try again',
          level: 'error',
          duration: 5000
        }));
        dispatch(setLoadingModule('addCustomer', false));
      });
  };

  const handleEnableUpload = () => {
    dispatch(setTenantEnableUpload(!tenantEnableUpload, true)); // true indicates user action
  };

  const handleTagSelected = (tag) => {
    console.log('tagging...', tag, customerIdsSelected);
    updateTenants(customerIdsSelected, tag);
    dispatch(setLoadingModule('tagCustomer', true));
    
    SPBackend.tagCustomers(tag._id, customerIdsSelected, user)
      .then((response) => {
        console.log('[handleTagSelected.tagCustomers] response: ', response);
        const { message, success } = response.data;
        dispatch(setSnackMessage({
          message: message,
          level: success ? 'success' : 'error',
          duration: 5000
        }));
        dispatch(setLoadingModule('tagCustomer', false));
      })
      .catch((error) => {
        log('[handleTagSelected.tagCustomers] error: ', error);
        updateTenants(customerIdsSelected, null);
        dispatch(setSnackMessage({
          message: error.response ? error.response.data : 'An error occured. Please try again',
          level: 'error',
          duration: 5000
        }));
      });
  };

  return (
    <AppBar 
      sx={classes.appBar}
      position="fixed"
    >
      <Toolbar
        sx={classes.toolbar}
        disableGutters
      >
        <Grid 
          container
          direction='column'
        >
          <HeaderTopBar
            usehandleDrawerToggle={usehandleDrawerToggle}
          >
            <Typography 
              sx={classes.pageTitle}
              variant='h2'
            >{i18n.t('Tenants')}</Typography>
          </HeaderTopBar>
          <Grid
            item
            sx={{
              ...classes.rowSub,
              ...classes.bottomBar
            }}
          >
            <Grid 
              container
              sx={classes.rowSubTypoCtr}
            >
              {
                customerIdsSelected && customerIdsSelected.length > 0
                ? <>
                  <Typography 
                    sx={{
                      ...classes.pageTitle,
                      ...classes.subHeaderCaption
                    }}
                    variant='h5'
                  >{customerIdsSelected.length} selected:</Typography>
                    <Box 
                      sx={classes.toggleContainer}
                    >
                      <Tooltip 
                        aria-label={`Tag ${customerIdsSelected.length} ${i18n.t('tenants')}`}
                        title={`Tag ${customerIdsSelected.length} ${i18n.t('tenants')}`} 
                      >
                        <Button
                          sx={classes.navButton}
                          onClick={() => setOpenTagPicker(!openTagPicker)}
                          ref={tagPickerAnchorRef}
                        >
                          <GroupIcon />
                          <div>{'Tag'}</div>
                        </Button>
                      </Tooltip>
                      <TagCustomersPicker
                        anchorEl={tagPickerAnchorRef.current}
                        open={openTagPicker}
                        setOpen={setOpenTagPicker}
                        tagActionCallback={handleTagSelected}
                      />
                    </Box>
                  </>
                : <>
                  <Box 
                    sx={classes.toggleContainer}
                  >
                    <Tooltip 
                      aria-label={`Add new ${i18n.t('tenant')}`}
                      title={`Add new ${i18n.t('tenant')}`} 
                    >
                      <Button
                        sx={classes.navButton}
                        onClick={() => setOpenAddTenantDialog(true)}
                      >
                        <i className={'icon-add'} />
                        <div>{i18n.t('Tenant')}</div>
                      </Button>
                    </Tooltip>
                  </Box>
                  <Box 
                    sx={classes.toggleContainer}
                  >
                    <Tooltip 
                      aria-label={`Upload ${i18n.t('tenants')}`}
                      title={`Upload ${i18n.t('tenants')}`} 
                    >
                      <Button
                        sx={tenantEnableUpload ? classes.navButtonActive : classes.navButton}
                        onClick={handleEnableUpload}
                      >
                        <PublishIcon />
                        <div>{'Upload'}</div>
                      </Button>
                    </Tooltip>
                  </Box>
                </>
              }
              <Box sx={classes.grow} />
              <FilterSearchBox
                  sx={{
                    root: classes.customerSearchBox
                  }}
                  handleSetSearchInputValue={handleSetSearchInputValue}
                  placeholderValue='Search by name or phone'
                  searchInputValue={searchTenantValue}
              />
            </Grid>
          </Grid>
          <AppLoadingSpinner />
        </Grid>
      </Toolbar>
      <AddTenantDialog
        open={openAddTenantDialog}
        setOpen={setOpenAddTenantDialog}
        triggerAction={handleAddTenant}
      />
    </AppBar>
  );
};

export default TenantsHeader;
